import classnames from "classnames";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { FOCUSABLE_ELEMENTS_SELECTOR } from "../../constants";
import useFocusTrap from "../../hooks/useFocusTrapping";
import Icon from "../Icon";
import "./dropdown.scss";

// @TODO:
// 1. Add active class
// 2. Add variation. Link or Button.
const Dropdown = ({ placeholder, items }) => {
  const [expanded, setExpanded] = useState();
  const [placeholderText, setPlaceholderText] = useState(placeholder);
  const [focusElement, setFocusElement] = useState(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const focusTrapRef = useFocusTrap(expanded, {
    focusSelector: focusElement,
  });

  const keyDownHandler = useCallback(
    ({ code }) => {
      if (expanded && code === "Escape") setExpanded(false);
    },
    [expanded]
  );

  const clickHandler = useCallback(
    ({ target }) => {
      if (expanded && target !== buttonRef.current) setExpanded(false);
    },
    [expanded]
  );

  const scrollTo = (href) => {
    const element = document.querySelector(href);
    if (element) {
      window.scrollTo({
        top: element.offsetTop,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (expanded) {
      const [firstElement] = listRef.current.querySelectorAll(
        FOCUSABLE_ELEMENTS_SELECTOR
      );
      setFocusElement(firstElement);

      document.addEventListener("click", clickHandler);
      document.addEventListener("keydown", keyDownHandler);
    }

    return () => {
      document.removeEventListener("click", clickHandler);
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [expanded, focusElement, keyDownHandler, clickHandler]);

  return (
    <div
      className={classnames("dropdown", { "dropdown--expanded": expanded })}
      ref={focusTrapRef}
    >
      <button
        ref={buttonRef}
        className="dropdown__button"
        aria-haspopup={true}
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <span>{placeholderText}</span>
        <Icon
          name={expanded ? "system-open-up" : "system-open-down"}
          size="extra-extra-small"
        />
      </button>
      {expanded && (
        <ul className="dropdown__list" tabIndex="-1" ref={listRef}>
          {items.map(({ id, href, text }) => (
            <li key={id}>
              <Link
                to={href}
                onClick={() => {
                  scrollTo(href);
                  setPlaceholderText(text);
                }}
              >
                {text}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

Dropdown.propTypes = {
  placeholder: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object),
};

Dropdown.defaultProps = {
  placeholder: "Cover Page",
  items: [],
};

export default Dropdown;
